import type { FC } from 'react'

import { useEffect } from 'react'
import { useRouter } from 'next/router'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

/*
 * Site-wide scroll motion, set up once per page. Opt elements in with data attributes:
 * data-reveal          fades and lifts in when scrolled into view (siblings are staggered)
 * data-reveal-image    wipes an image frame open from the bottom
 * data-parallax="12"   drifts an element by the given percentage while its parent scrolls past
 * data-count           counts a value such as "$98M" or "120+" up from zero once
 * Elements already on screen when the page loads are left as they are, so nothing flickers.
 * Everything is skipped when the visitor prefers reduced motion.
 */

const COUNT_PATTERN = /^(\D*)([\d.,]+)(.*)$/

const isBelowFold = (element: Element): boolean => element.getBoundingClientRect().top > window.innerHeight * 0.9

export const Motion: FC = () => {
    const router = useRouter()
    const path = router.asPath.split('#')[0]

    useEffect(() => {
        gsap.registerPlugin(ScrollTrigger)

        const media = gsap.matchMedia()

        media.add('(prefers-reduced-motion: no-preference)', () => {
            const restore: (() => void)[] = []
            const reveals = gsap.utils.toArray<HTMLElement>('[data-reveal]').filter(isBelowFold)

            if (reveals.length) {
                gsap.set(reveals, { autoAlpha: 0, y: 24 })

                ScrollTrigger.batch(reveals, {
                    start: 'top 90%',
                    once: true,
                    onEnter: (batch) => gsap.to(batch, {
                        autoAlpha: 1,
                        y: 0,
                        duration: 0.8,
                        ease: 'power3.out',
                        stagger: 0.08,
                        overwrite: true
                    })
                })
            }

            gsap.utils.toArray<HTMLElement>('[data-reveal-image]').filter(isBelowFold).forEach((element) => {
                const image = element.querySelector('img')
                const timeline = gsap.timeline({ scrollTrigger: { trigger: element, start: 'top 88%', once: true } })

                timeline.fromTo(
                    element,
                    { clipPath: 'inset(100% 0% 0% 0%)' },
                    { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.1, ease: 'power3.inOut' }
                )

                if (image) {
                    timeline.fromTo(
                        image,
                        { scale: 1.1 },
                        { scale: 1, duration: 1.5, ease: 'expo.out', clearProps: 'transform' },
                        0
                    )
                }
            })

            gsap.utils.toArray<HTMLElement>('[data-parallax]').forEach((element) => {
                const amount = Number(element.dataset.parallax) || 10

                gsap.fromTo(
                    element,
                    { yPercent: -amount / 2 },
                    {
                        yPercent: amount / 2,
                        ease: 'none',
                        scrollTrigger: {
                            trigger: element.parentElement ?? element,
                            start: 'top bottom',
                            end: 'bottom top',
                            scrub: true
                        }
                    }
                )
            })

            gsap.utils.toArray<HTMLElement>('[data-count]').filter(isBelowFold).forEach((element) => {
                const original = element.textContent ?? ''
                const match = COUNT_PATTERN.exec(original.trim())

                if (!match) return

                const [, prefix, digits, suffix] = match
                const target = Number(digits.replace(/,/g, ''))
                const decimals = digits.includes('.') ? digits.split('.')[1].length : 0

                if (!Number.isFinite(target)) return

                restore.push(() => {
                    element.textContent = original
                })

                const counter = { value: 0 }
                const render = () => {
                    element.textContent = `${prefix}${counter.value.toFixed(decimals)}${suffix}`
                }

                render()

                gsap.to(counter, {
                    value: target,
                    duration: 1.8,
                    ease: 'power2.out',
                    onUpdate: render,
                    onComplete: () => {
                        element.textContent = original
                    },
                    scrollTrigger: { trigger: element, start: 'top 85%', once: true }
                })
            })

            const frame = requestAnimationFrame(() => ScrollTrigger.refresh())

            return () => {
                cancelAnimationFrame(frame)
                restore.forEach((undo) => undo())
            }
        })

        return () => media.revert()
    }, [path])

    return null
}
